import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Product, RbacRole, RbacRoleLabel } from '@axilon-apps/domains-identity/constants'
import {
  useCreateRoleAssignmentMutation,
  useDeleteRoleAssignmentMutation,
  useGetGroupedRoleAssignmentsQuery,
  useGetRolesQuery,
  useLazyGetUsersQuery
} from '@axilon-apps/domains-identity/state'
import type { Model } from '@axilon-apps/domains-identity/types'
import { UserAvatar } from '@axilon-apps/libs-ui-components'

import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import { Label } from '../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table'
import { UserCombobox } from './UserCombobox'

const PER_PAGE = 100

function errorMessage(error: unknown, action: 'assign' | 'remove') {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: number }).status
    if (status === 409) return 'This user already has that role.'
    if (status === 403) return `You are not allowed to ${action} role assignments.`
  }
  return `Unable to ${action} the role assignment. Please try again.`
}

export function rolesVisibleToCaller(
  roles: Model.RbacRoleResponse[],
  isGlobalAdmin: boolean
): Model.RbacRoleResponse[] {
  return isGlobalAdmin ? roles : roles.filter((role) => role.name !== RbacRole.GlobalAdmin)
}

export function RoleAssignmentsTab({ isGlobalAdmin }: { isGlobalAdmin: boolean }) {
  const [page, setPage] = useState(1)
  const [userId, setUserId] = useState<string | undefined>()
  const [roleName, setRoleName] = useState<string | undefined>()
  const [enabled, setEnabled] = useState<boolean | undefined>()
  const [isAssignOpen, setIsAssignOpen] = useState(false)
  const [assignmentError, setAssignmentError] = useState<string>()
  const [removalError, setRemovalError] = useState<string>()
  const [users, setUsers] = useState<Model.User[]>([])
  const [getUsers] = useLazyGetUsersQuery()

  const assignments = useGetGroupedRoleAssignmentsQuery({
    product: [Product.Ontology],
    page,
    perPage: PER_PAGE
  })
  const roles = useGetRolesQuery({ product: [Product.Ontology], page: 1, perPage: 100 })
  const [deleteAssignment, deleteState] = useDeleteRoleAssignmentMutation()
  const availableRoles = useMemo(
    () => rolesVisibleToCaller(roles.data?.items ?? [], isGlobalAdmin),
    [isGlobalAdmin, roles.data?.items]
  )

  useEffect(() => {
    let cancelled = false

    const loadUsers = async () => {
      try {
        const firstPage = await getUsers({ page: 1, perPage: PER_PAGE }).unwrap()
        const remainingPages = await Promise.all(
          Array.from({ length: Math.max(0, firstPage.pages - 1) }, (_, index) =>
            getUsers({ page: index + 2, perPage: PER_PAGE }).unwrap()
          )
        )

        if (!cancelled) {
          setUsers([
            ...(firstPage.items as Model.User[]),
            ...remainingPages.flatMap((result) => result.items as Model.User[])
          ])
        }
      } catch {
        if (!cancelled) setUsers([])
      }
    }

    void loadUsers()
    return () => {
      cancelled = true
    }
  }, [getUsers])

  const hiddenGlobalAdminUserIds = useMemo(
    () =>
      new Set(
        users
          .filter((user) =>
            user.roleAssignments?.some(
              (assignment) => assignment.role.name === RbacRole.GlobalAdmin
            )
          )
          .map((user) => user.id)
      ),
    [users]
  )
  const visibleUsers = useMemo(
    () => (isGlobalAdmin ? users : users.filter((user) => !hiddenGlobalAdminUserIds.has(user.id))),
    [hiddenGlobalAdminUserIds, isGlobalAdmin, users]
  )
  const usersById = useMemo(
    () => new Map(visibleUsers.map((user) => [user.id, user])),
    [visibleUsers]
  )

  const filteredAssignmentGroups = useMemo(
    () =>
      (assignments.data?.items ?? [])
        .filter((group) => isGlobalAdmin || group.role.name !== RbacRole.GlobalAdmin)
        .filter((group) => !roleName || group.role.name === roleName)
        .map((group) => ({
          ...group,
          assignments: group.assignments.filter((assignment) => {
            if (!isGlobalAdmin && hiddenGlobalAdminUserIds.has(assignment.userId)) return false
            if (userId && assignment.userId !== userId) return false

            const assignedUser = usersById.get(assignment.userId)
            return enabled === undefined || assignedUser?.enabled === enabled
          })
        }))
        .filter((group) => group.assignments.length > 0),
    [
      assignments.data?.items,
      enabled,
      hiddenGlobalAdminUserIds,
      isGlobalAdmin,
      roleName,
      userId,
      usersById
    ]
  )

  const setFilter = (setter: (value: string | undefined) => void, value: string) => {
    setter(value || undefined)
    setPage(1)
  }

  const removeAssignment = async (assignmentId: string, role: RbacRole) => {
    if (!window.confirm(`Remove the ${RbacRoleLabel[role]} role assignment?`)) return

    setRemovalError(undefined)
    try {
      await deleteAssignment(assignmentId).unwrap()
    } catch (error) {
      setRemovalError(errorMessage(error, 'remove'))
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Role Assignments</CardTitle>
            <p className="mt-1 text-xs font-normal text-ink-3">
              View and manage the roles users have in Ontology.
            </p>
          </div>
          <Button size="sm" onClick={() => setIsAssignOpen(true)}>
            <Plus className="size-3.5" /> Assign role
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-3">
            <div className="grid w-full gap-1.5 sm:w-48">
              <Label htmlFor="assignment-user-filter">User</Label>
              <UserCombobox
                id="assignment-user-filter"
                users={visibleUsers}
                value={userId ?? ''}
                onValueChange={(value) => setFilter(setUserId, value ?? '')}
                placeholder="All users"
                includeAll
              />
            </div>
            <div className="grid w-full gap-1.5 sm:w-48">
              <Label htmlFor="assignment-role-filter">Role</Label>
              <Select
                value={roleName ?? ''}
                onValueChange={(value) => setFilter(setRoleName, value === 'all' ? '' : value)}
              >
                <SelectTrigger id="assignment-role-filter" className="w-full">
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {availableRoles.map((role) => (
                    <SelectItem key={role.id} value={role.name}>
                      {RbacRoleLabel[role.name]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid w-full gap-1.5 sm:w-48">
              <Label htmlFor="assignment-status-filter">Status</Label>
              <Select
                value={enabled === undefined ? 'all' : String(enabled)}
                onValueChange={(value) => {
                  setEnabled(value === 'all' ? undefined : value === 'true')
                  setPage(1)
                }}
              >
                <SelectTrigger id="assignment-status-filter" className="w-full">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {assignments.error && (
            <p className="text-sm text-st-down">Unable to load role assignments.</p>
          )}
          {removalError && (
            <p role="alert" className="text-sm text-st-down">
              {removalError}
            </p>
          )}

          {assignments.isLoading ? (
            <div className="py-8 text-center text-sm text-ink-3">Loading role assignments…</div>
          ) : filteredAssignmentGroups.length === 0 ? (
            <div className="py-8 text-center text-sm text-ink-3">No role assignments found.</div>
          ) : (
            <div className="flex flex-col gap-5">
              {filteredAssignmentGroups.map((group) => (
                <section key={group.role.id} className="overflow-hidden rounded-lg border">
                  <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-3">
                    <Badge>{RbacRoleLabel[group.role.name]}</Badge>
                    <span className="text-xs text-ink-3">
                      {group.assignments.length} {group.assignments.length === 1 ? 'user' : 'users'}
                    </span>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Assigned</TableHead>
                        <TableHead className="w-12" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.assignments.map((assignment) => {
                        const assignedUser = usersById.get(assignment.userId)
                        return (
                          <TableRow key={assignment.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {assignedUser && <UserAvatar user={assignedUser} size={32} />}
                                <span>{assignedUser?.username ?? assignment.userId}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {new Date(assignment.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon-xs"
                                aria-label={`Remove ${RbacRoleLabel[group.role.name]} role`}
                                disabled={deleteState.isLoading}
                                onClick={() => removeAssignment(assignment.id, group.role.name)}
                              >
                                <Trash2 />
                              </Button>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </section>
              ))}
            </div>
          )}

          {(assignments.data?.hasPrev || assignments.data?.hasNext) && (
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!assignments.data?.hasPrev}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!assignments.data?.hasNext}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <AssignRoleDialog
        open={isAssignOpen}
        onOpenChange={setIsAssignOpen}
        users={visibleUsers}
        roles={availableRoles}
        error={assignmentError}
        onError={setAssignmentError}
      />
    </div>
  )
}

type AssignRoleDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  users: Model.User[]
  roles: Model.RbacRoleResponse[]
  error?: string
  onError: (error?: string) => void
}

function AssignRoleDialog({
  open,
  onOpenChange,
  users,
  roles,
  error,
  onError
}: AssignRoleDialogProps) {
  const [userId, setUserId] = useState('')
  const [roleId, setRoleId] = useState('')
  const [createAssignment, createState] = useCreateRoleAssignmentMutation()

  const close = () => {
    setUserId('')
    setRoleId('')
    onError(undefined)
    onOpenChange(false)
  }

  const assign = async () => {
    if (!userId || !roleId) return
    onError(undefined)
    try {
      await createAssignment({ userId, roleId, scope: [] }).unwrap()
      close()
    } catch (requestError) {
      onError(errorMessage(requestError, 'assign'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="w-[30rem] max-w-[calc(100vw-2rem)] gap-5">
        <DialogHeader>
          <DialogTitle>Assign role</DialogTitle>
          <DialogDescription>Role assignments apply to the Ontology application.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-5">
          <div className="grid gap-1.5">
            <Label htmlFor="assignment-user">User</Label>
            <UserCombobox
              id="assignment-user"
              users={users}
              value={userId}
              onValueChange={(value) => setUserId(value ?? '')}
              placeholder="Select a user"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="assignment-role">Role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger id="assignment-role" className="w-full">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {RbacRoleLabel[role.name]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {error && (
            <p role="alert" className="text-sm text-st-down">
              {error}
            </p>
          )}
        </div>
        <DialogFooter className="mt-1">
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={assign} disabled={!userId || !roleId || createState.isLoading}>
            {createState.isLoading ? 'Assigning…' : 'Assign role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
